const prefix = 'adn'; //the project-specific prefix applied to data attributes generated by validation methods

export { FormValidator, PhoneValidator, PostalCodeValidator, InvalidEmail };

function PhoneValidator(input) {
	return function () {

		const length = input.value.length;
		const countryCode = input.value.startsWith('1');

		//add dashes (and optional extension comma) for phone
		if (!input.value.includes('-') && input.value.length) {
			if (countryCode) {
				input.value = `${input.value.substr(0, 1)}-${input.value.substr(1, 3)}-${input.value.substr(4, 3)}-${input.value.substr(7, 4)}${length > 11 ? `,${input.value.substr(11, 4)}` : ''}`;
			} else {
				input.value = `${input.value.substr(0, 3)}-${input.value.substr(3, 3)}-${input.value.substr(6, 4)}${length > 10 ? `,${input.value.substr(10, 4)}` : ''}`;
			}
		}

		//add comma when phone has already been formatted with dashes
		if (!input.value.includes(',') && ((countryCode && input.value.length > 14) || (!countryCode && input.value.length > 12))) {
			if (countryCode) {
				input.value = `${input.value.substr(0, 14)},${input.value.substr(14, 4)}`;
			} else {
				input.value = `${input.value.substr(0, 12)},${input.value.substr(12, 4)}`;
			}
		}
	}
}

function PostalCodeValidator(input) {
	return function () {
		//add space and uppercase letters for postal code
		if (input.value.length && !input.value.includes(' ')) {
			input.value = `${input.value.substr(0, 3).toUpperCase()} ${input.value.substr(3, 3).toUpperCase()}`;
		}
	};
}

function InvalidEmail(value) {

	if (!value.includes('@')) {
		return 'Email must contain an @ symbol.';
	}

	const parts = value.split('@');
	const user = parts[0];
	const domain = parts[1];

	//check for more than one @ symbol
	if (parts.length != 2) {
		return 'Invalid email address.';
	}

	//check for min/max lengths
	if (user.length < 1) {
		return 'Email preceeding the @ symbol must be at least 1 character long.'
	}
	if (user.length > 64) {
		return 'Email must not exceed 64 characters preceeding the @ symbol.'
	}
	if (domain.length < 3) {
		return 'Email following the @ symbol must be at least 3 characters long.'
	}
	if (domain.length > 255) {
		return 'Email must not exceed 255 characters following the @ symbol.'
	}

	//check for valid characters
	const regex = new RegExp("^[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~.]+$");
	if (!regex.test(user) || !regex.test(domain)) {
		return 'Email contains invalid characters.';
	}

	//check for specific character combinations/positions
	if (user.includes('..') || domain.includes('..')) {
		return 'Email cannot contain adjacent periods.';
	}
	if (user[0] == '.' || user[user.length - 1] == '.') {
		return 'Email cannot start or end with a period.';
	}
	if (!domain.includes('.')) {
		return 'Email domain must include a period, eg. example.com'
	}

	if (domain.includes('--')) {
		return 'Email domain cannot contain adjacent hyphens.';
	}
	if (domain[0] == '.' || domain[domain.length - 1] == '.') {
		return 'Email domain cannot start or end with a period.';
	}

	//check for all-numbers
	const domainParts = domain.split('.');
	if (!isNaN(user) || !isNaN(domain) || !isNaN(domainParts[1])) {
		return 'Email parts must not be entirely numbers.';
	}

	//email is not invalid
	return false;
}


class FormValidator {
	constructor(form, options = {}) {
		var instance = this;

		this.form = form;
		this.options = options;

		this.selector = 'input:not([type="hidden"]),textarea,select:not(.no-validate)';
		if (this.options.selector) {
			this.selector = this.options.selector;
		}

		this.custom = false;
		if (this.options.fields) {
			this.custom = this.options.fields.length > 0;
		}

		this.context = form;
		if (this.options.context) {
			this.context = this.options.context;
		}

		//manages scrolling page to the first invalid field
		this.first = true;
		this.scroll = true;

		if (this.options.scroll === false) {
			//scroll to error disabled
			this.scroll = false;

		} else if (this.options.scroll) {
			//scroll within the supplied parent element
			if (this.options.scroll.context) {
				this.scrollContext = this.options.scroll.context
			}
		}

		//disable default js validation of the form
		this.form.noValidate = true;

		//bind re-validation of the custom field to the provided events of the DOM element
		if (this.custom) {
			for (const field of this.options.fields) {
				if (field.events) {
					const selector = this.#fieldSelector(field);
					const input = this.context.querySelector(selector);
					if (input) {
						field.events.forEach(e => input.addEventListener(e, () => instance.#validateCustom(field) ));
					}
					//else field not found in the DOM
				}
			}
		}
	}

	/**
	 * Checks validity and applies validation errors to the provided inputs.
	 *
	 * @param {String} selector An optional selector of specific fields to validate.
	 */
	async valid(selector) {
		var instance = this;

		let valid = this.form.checkValidity();
		if (!valid || this.custom) {

			this.first = true;

			const validated = this.context.classList.contains('was-validated');
			if (!validated && selector) {
				//do not scroll to input until a full form validation has occurred
				//we check for selector because was-validated is not applied until after validation has occurred
				this.first = false;
			}

			if (this.options.debug) {
				console.log(`%cbegin form validation (${this.form.getAttribute('id')})`, 'color: #47ce38');
			}

			if (!selector) {

				//validate all form fields
				this.context.querySelectorAll(this.selector).forEach((input) => {
					if (!instance.#hasCustom(input)) {
						instance.#validateInput(input);
					}
				});

				//validate all custom validation fields
				if (this.options.fields) {
					await Promise.all(
						this.options.fields.map(async (field) => {
							if (!await instance.#validateCustom(field)) {
								valid = false;
							}
						})
					);
				}

			} else {

				//only validate elements returned by the provided selector
				await Promise.all(
					[...this.context.querySelectorAll(selector)].map(async (element) => {
						const field = instance.#hasCustom(element);
						if (field) {
							if (!await instance.#validateCustom(field)) {
								valid = false;
							}
						} else {
							instance.#validateInput(element);
						}
					})
				);
			}

			if (this.options.debug) {
				console.log(`%cend form validation (${this.form.getAttribute('id')})`, 'color: #0c77d5');
			}
		}

		if (!valid) {
			//make sure validation errors are visible
			this.context.classList.add('was-validated');
		}

		return valid;
	}

	/**
	 * Returns the custom validation implementation when defined, else undefined.
	 */
	#hasCustom(element) {

		if (!this.options.fields) {
			return false;
		}

		return [...this.options.fields].find(f => f.id === element.getAttribute('id'));
	}

	/**
	 * Scrolls to first invalid input (when not already scrolled).
	 */
	#scrollToInput(input) {

		if (this.first && this.scroll) {
			let modal = input.closest('.modal');

			if (this.scrollContext) {
				//custom parent element to scroll supplied
				let top = -(this.scrollContext.getBoundingClientRect().top - input.getBoundingClientRect().top);
				this.scrollContext.scrollBy({
					top: top - 40,
					behavior: 'smooth'
				});

			} else if (modal) {
				//default to scrolling the modal content when in a modal
				let top = -(modal.getBoundingClientRect().top - input.getBoundingClientRect().top);
				modal.scrollBy({
					top: top - 40,
					behavior: 'smooth'
				});

			} else {
				window.scrollBy({
					top: input.getBoundingClientRect().top - 80,
					behavior: 'smooth'
				});
			}
			this.first = false;
		}
	}

	#validateInput(input) {
		var instance = this;

		var field = { custom: false };
		if (this.options.fields) {
			field = [...this.options.fields].find((element) => element.name === input.name) || { custom: false };
		}

		//generate hidden invalid feedback
		const error = this.#formInputFeedback(input, field);

		//validate
		let validInput;
		if (!field.custom) {
			validInput = input.checkValidity();

		} else {
			if (field.hasOwnProperty('valid')) {
				//run custom validation
				validInput = field.valid(input);
				input.setCustomValidity(validInput ? '' : error);
			}
		}

		//toggle validation on an element before the form is validated
		const tags = input.parentElement.querySelector('tags');
		if (tags) {
			tags.classList.toggle('is-invalid', !validInput);
			tags.classList.toggle('is-valid', validInput);
		}

		if (!validInput) {
			this.#scrollToInput(input);

			if (this.options.debug) {
				console.log(`${error} (${input.name})`);
			}
		}

		return validInput;
	}

	async #validateCustom(field) {
		var instance = this;
		const selector = this.#fieldSelector(field);

		const input = document.querySelector(selector);
		if (!input) {
			if (this.options.debug) {
				console.warn(`Custom field supplied in FormValidator options was not found: ${selector}.`);
			}
			return true;
		}

		//validate
		let validInput;
		if (!field.hasOwnProperty('valid')) {
			console.error("Custom fields must define a function property 'valid'.");
		} else {
			//prepare JS constraint validation
			if (input.validity) {
				input.setCustomValidity('');
			}

			if (input.disabled) {
				//skip custom validation for disabled inputs
				validInput = true;
			} else {
				//run custom validation
				validInput = await field.valid(input, field);
			}

			//generate hidden invalid feedback
			const error = this.#formInputFeedback(input, field);

			//render validation result
			if (input.validity) {
				//input supports JS constraint validation
				input.setCustomValidity(validInput ? '' : error);

				if (field.events?.length) {
					input.classList.toggle('is-invalid', !validInput);
					input.classList.toggle('is-valid', validInput);
				}

			} else {
				//input is some other element, flag validity on the hidden input generated for this custom validation method
				let context;
				if (field.hasOwnProperty('context')) {
					context = field.context;
				} else {
					context = input.parentElement;
				}

				const invalid = context.querySelector(`[data-${prefix}-invalid-input]`);
				invalid.classList.toggle('is-invalid', !validInput);
				invalid.classList.toggle('is-valid', validInput);
			}

			if (!validInput) {
				this.#scrollToInput(input);

				if (this.options.debug) {
					console.log(`${error} (${input.name})`);
				}
			}
		}

		return validInput;
	}

	/**
	 * Returns the selector for the field's DOM element based on the id or name properties.
	 */
	#fieldSelector(field) {
		if (field.id) {
			return `#${field.id}`;
		} else if (field.name) {
			return `[name="${field.name}"]`;
		}
	}

	/**
	 * Generates the invalid feedback for the input without showing it.
	 */
	#formInputFeedback(input, field) {

		//determine parent element to generate error within
		let context, group;
		if (field.hasOwnProperty('context')) {
			//custom parent element specified
			context = field.context;

		} else if (input.type === 'radio' || input.type === 'checkbox') {
			context = input.closest(`[data-${prefix}-validation-group]`);
			group = (context !== null);
		}

		if (!context) {
			//default to input's parent element
			context = input.parentElement;
		}

		//check for existing default error element
		let feedback = context.querySelector(`:scope > .invalid-feedback:not([data-${prefix}-invalid])`);
		if (feedback) {
			//make sure default feedback is hidden to prepare for generated one
			feedback.classList.add('d-none');
		}

		//check for existing error element (feedback) or create new one
		feedback = context.querySelector(`:scope > .invalid-feedback[data-${prefix}-invalid]`);
		if (!feedback) {
			if (input.nodeName.toLowerCase() === 'div' || input.nodeName.toLowerCase() === 'form') {
				//prepend input to show invalid feedback if the non-input custom validation method is invalid
				const invalid = document.createElement('input');
				invalid.type = 'hidden';
				invalid.setAttribute(`data-${prefix}-invalid-input`, '');
				context.append(invalid);
			}

			feedback = document.createElement('div');
			feedback.classList = 'invalid-feedback';
			feedback.setAttribute(`data-${prefix}-invalid`, '');
			context.append(feedback);
		}

		//prepend input for selecting :invalid input group
		let groupInput = context.querySelector(`:scope > .${prefix}-group-${CSS.escape(input.name)}`);
		if (group && !groupInput) {
			let groupInput = document.createElement('input');
			groupInput.type = input.type;
			groupInput.name = input.name;
			groupInput.classList = `${prefix}-group ${prefix}-group-${input.name} d-none`;
			context.prepend(groupInput);
		}

		// if (!feedback.hasAttribute(`data-${prefix}-invalid`)) {
		// 	//only generate errors for designated fields
		// 	return;
		// }

		let error;
		if (input.validity && input.validity.customError) {
			error = input.validationMessage;
		} else {
			//determine field name
			let name = context.getAttribute(`data-${prefix}-validation-group`);
			if (!name) {
				name = this.#formInputName(input);
			}

			//determine error message
			if (field.hasOwnProperty('error')) {
				error = field.error(input);
			}

			if (!error) {
				error = this.#formInputError(name, input);
			}
		}

		feedback.innerHTML = error;
		return error;
	}

	/**
	 * Returns the default error message based on the provided name and input's validation state.
	 */
	#formInputError(name, input) {
		const feedback = input.parentElement.querySelector(`.invalid-feedback:not([data-${prefix}-invalid])`);
		if (feedback) {
			return feedback.innerText;
		} else {
			return `${name} ${this.#formInputSummary(input)}`;
		}
	}


	/**
	 * Returns the default field name (label) based on the provided input's label.
	 */
	#formInputName(input) {
		let name = '';
		if (!input || !input.parentElement) {
			name = 'The field';
		}

		const label = document.querySelector(`label[for="${input.id}"]`);
		if (label?.hasAttribute(`data-${prefix}-label`)) {
			name = label.getAttribute(`data-${prefix}-label`);

		} else if (input.hasAttribute(`data-${prefix}-label`)) {
			name = input.getAttribute(`data-${prefix}-label`);

		} else if (label) {
			name = label.textContent;

		} else if (input.placeholder) {
			name = input.placeholder;
		}

		return name;
	}

	/**
	 * Returns the default error message based on the provided input's validation state.
	 */
	#formInputSummary(input) {
		let description = 'is invalid.';
		if (input) {
			if (input.required && !input.value) {
				description = 'is required.';
			}
		}
		return description;
	}
}
